import { createBrowserRouter, Navigate } from 'react-router-dom';
import { Suspense } from 'react';
import { Loading } from '@/components/loading';
import { AuthGuard } from '@/components/auth-guard';
import { lazyWithRetry, preloadRoute } from '@/utils/lazy-with-retry';
import { RouteErrorBoundary } from '@/components/route-error-boundary';

// Layouts (not used in current implementation)

// Auth Pages
const LoginAuthPage = lazyWithRetry(() => import('@/pages/auth/login'));
const RegisterAuthPage = lazyWithRetry(() => import('@/pages/auth/register'));
const ResetPasswordPage = lazyWithRetry(
  () => import('@/pages/auth/reset-password')
);
const EmailVerificationPage = lazyWithRetry(
  () => import('@/pages/auth/email-verification')
);
const VerificationSuccessPage = lazyWithRetry(
  () => import('@/pages/auth/verification-success')
);
const NotFoundPage = lazyWithRetry(() => import('@/pages/not-found-page'));

// Main Pages
const HomePage = lazyWithRetry(() => import('@/pages/home'));
const DashboardPage = lazyWithRetry(() => import('@/pages/dashboard'));
const DeviceTestPage = lazyWithRetry(() => import('@/pages/device-test'));

// Vehicle Pages
const VehiclesPage = lazyWithRetry(() => import('@/pages/vehicles'));
const NewVehiclePage = lazyWithRetry(() => import('@/pages/vehicles/new'));
const VehicleDetailPage = lazyWithRetry(() => import('@/pages/vehicles/[id]'));
const EditVehiclePage = lazyWithRetry(
  () => import('@/pages/vehicles/[id]/edit')
);

// Reminder Pages
// const RemindersPage = lazyWithRetry(() => import('@/pages/reminders'));
// const NewReminderPage = lazyWithRetry(() => import('@/pages/reminders/new'));
// const AutoSetupRemindersPage = lazyWithRetry(
//   () => import('@/pages/reminders/auto-setup')
// );

// Profile Page
const ProfilePage = lazyWithRetry(() => import('@/pages/profile'));

// Map Page
const V16MapPage = lazyWithRetry(() => import('@/pages/vehicles/[id]/map'));

// Insurance Pages
const VehicleInsurancePage = lazyWithRetry(
  () => import('@/pages/vehicles/[id]/insurance')
);
const EditVehicleInsurancePage = lazyWithRetry(
  () => import('@/pages/vehicles/[id]/insurance/edit')
);

// Legal Pages
const TermsOfServicePage = lazyWithRetry(
  () => import('@/pages/legal/terms-of-service-full')
);
const PrivacyPolicyPage = lazyWithRetry(
  () => import('@/pages/legal/privacy-policy-full')
);
const CookiePolicyPage = lazyWithRetry(
  () => import('@/pages/legal/cookie-policy-full')
);
const DisclaimerPage = lazyWithRetry(() => import('@/pages/legal/disclaimer'));

// Emergency Guide Page
const EmergencyGuidePage = lazyWithRetry(
  () => import('@/pages/emergency-guide')
);

// Quick Links Page
const QuickLinksPage = lazyWithRetry(() => import('@/pages/quick-links'));

// About Page
const AboutPage = lazyWithRetry(() => import('@/pages/about'));

// Preload critical routes
if (typeof window !== 'undefined') {
  preloadRoute(() => import('@/pages/home'));
  preloadRoute(() => import('@/pages/auth/login'));
}

export const router = createBrowserRouter([
  {
    path: '/',
    element: (
      <Suspense fallback={<Loading />}>
        <HomePage />
      </Suspense>
    ),
  },
  {
    path: '/home',
    element: <Navigate to='/' replace />,
  },
  {
    path: '/dashboard',
    element: (
      <AuthGuard requireAuth={true}>
        <Suspense fallback={<Loading />}>
          <DashboardPage />
        </Suspense>
      </AuthGuard>
    ),
  },
  {
    path: '/auth',
    element: <Navigate to='/auth/login' replace />,
  },
  {
    path: '/auth/login',
    element: (
      <AuthGuard requireAuth={false}>
        <Suspense fallback={<Loading />}>
          <LoginAuthPage />
        </Suspense>
      </AuthGuard>
    ),
  },
  {
    path: '/auth/register',
    element: (
      <AuthGuard requireAuth={false}>
        <Suspense fallback={<Loading />}>
          <RegisterAuthPage />
        </Suspense>
      </AuthGuard>
    ),
  },
  {
    path: '/auth/reset-password',
    element: (
      <AuthGuard requireAuth={false}>
        <Suspense fallback={<Loading />}>
          <ResetPasswordPage />
        </Suspense>
      </AuthGuard>
    ),
  },
  {
    path: '/auth/email-verification',
    element: (
      <AuthGuard requireAuth={false}>
        <Suspense fallback={<Loading />}>
          <EmailVerificationPage />
        </Suspense>
      </AuthGuard>
    ),
  },
  {
    path: '/auth/verification-success',
    element: (
      <AuthGuard requireAuth={false}>
        <Suspense fallback={<Loading />}>
          <VerificationSuccessPage />
        </Suspense>
      </AuthGuard>
    ),
  },
  {
    path: '/login',
    element: <Navigate to='/auth/login' replace />,
  },
  {
    path: '/register',
    element: <Navigate to='/auth/register' replace />,
  },
  {
    path: '/vehicles',
    element: (
      <AuthGuard requireAuth={true}>
        <Suspense fallback={<Loading />}>
          <VehiclesPage />
        </Suspense>
      </AuthGuard>
    ),
  },
  {
    path: '/vehicles/new',
    element: (
      <AuthGuard requireAuth={true}>
        <Suspense fallback={<Loading />}>
          <NewVehiclePage />
        </Suspense>
      </AuthGuard>
    ),
  },
  {
    path: '/vehicles/:id',
    element: (
      <AuthGuard requireAuth={true}>
        <Suspense fallback={<Loading />}>
          <VehicleDetailPage />
        </Suspense>
      </AuthGuard>
    ),
  },
  {
    path: '/vehicles/:id/edit',
    element: (
      <AuthGuard requireAuth={true}>
        <Suspense fallback={<Loading />}>
          <EditVehiclePage />
        </Suspense>
      </AuthGuard>
    ),
  },
  {
    path: '/vehicles/:id/map',
    element: (
      <AuthGuard requireAuth={true}>
        <Suspense fallback={<Loading />}>
          <V16MapPage />
        </Suspense>
      </AuthGuard>
    ),
  },
  {
    path: '/vehicles/:id/insurance',
    element: (
      <AuthGuard requireAuth={true}>
        <Suspense fallback={<Loading />}>
          <VehicleInsurancePage />
        </Suspense>
      </AuthGuard>
    ),
  },
  {
    path: '/vehicles/:id/insurance/edit',
    element: (
      <AuthGuard requireAuth={true}>
        <Suspense fallback={<Loading />}>
          <EditVehicleInsurancePage />
        </Suspense>
      </AuthGuard>
    ),
  },
  // {
  //   path: '/reminders',
  //   element: (
  //     <AuthGuard requireAuth={true}>
  //       <Suspense fallback={<Loading />}>
  //         <RemindersPage />
  //       </Suspense>
  //     </AuthGuard>
  //   ),
  // },
  // {
  //   path: '/reminders/new',
  //   element: (
  //     <AuthGuard requireAuth={true}>
  //       <Suspense fallback={<Loading />}>
  //         <NewReminderPage />
  //       </Suspense>
  //     </AuthGuard>
  //   ),
  // },
  // {
  //   path: '/reminders/auto-setup',
  //   element: (
  //     <AuthGuard requireAuth={true}>
  //       <Suspense fallback={<Loading />}>
  //         <AutoSetupRemindersPage />
  //       </Suspense>
  //     </AuthGuard>
  //   ),
  // },
  {
    path: '/profile',
    element: (
      <AuthGuard requireAuth={true}>
        <Suspense fallback={<Loading />}>
          <ProfilePage />
        </Suspense>
      </AuthGuard>
    ),
  },
  {
    path: '/account',
    element: <Navigate to='/profile' replace />,
  },
  {
    path: '/emergency-guide',
    element: (
      <Suspense fallback={<Loading />}>
        <EmergencyGuidePage />
      </Suspense>
    ),
  },
  {
    path: '/quick-links',
    element: (
      <Suspense fallback={<Loading />}>
        <QuickLinksPage />
      </Suspense>
    ),
  },
  {
    path: '/about',
    element: (
      <Suspense fallback={<Loading />}>
        <AboutPage />
      </Suspense>
    ),
  },
  {
    path: '/enlaces-rapidos',
    element: <Navigate to='/quick-links' replace />,
  },
  {
    path: '/device-test',
    element: <Navigate to='/prueba-baliza-v16' replace />,
  },
  {
    path: '/prueba-baliza-v16',
    element: (
      <Suspense fallback={<Loading />}>
        <DeviceTestPage />
      </Suspense>
    ),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: '/terms-of-service',
    element: (
      <Suspense fallback={<Loading />}>
        <TermsOfServicePage />
      </Suspense>
    ),
  },
  {
    path: '/privacy-policy',
    element: (
      <Suspense fallback={<Loading />}>
        <PrivacyPolicyPage />
      </Suspense>
    ),
  },
  {
    path: '/cookie-policy',
    element: (
      <Suspense fallback={<Loading />}>
        <CookiePolicyPage />
      </Suspense>
    ),
  },
  {
    path: '/disclaimer',
    element: (
      <Suspense fallback={<Loading />}>
        <DisclaimerPage />
      </Suspense>
    ),
  },
  {
    path: '/legal-disclaimer',
    element: <Navigate to='/disclaimer' replace />,
  },
  {
    path: '*',
    element: (
      <Suspense fallback={<Loading />}>
        <NotFoundPage />
      </Suspense>
    ),
  },
]);
